import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, CardHeader, ErrorState, Input, MoneyInput, PageHeader, PageLoader, Select, Switch, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { FormSection, KeyValues, TwoColumn } from '../../../components/common/Parts/Parts';
import { useFetch } from '../../../hooks/useFetch';
import { masterEntry, masterOptions } from '../../masters/services/masterService';
import { useMasters } from '../../masters/hooks/useMasters';
import { serverFieldErrors, trimOrUndef } from '../../../utils/validation';
import { useForm } from '../../../hooks/useForm';
import { money } from '../../../utils/currency';
import { itemService } from '../services/materialService';
import type { ChangeEvent } from 'react';
import type { Item } from '../types/materialTypes';
import type { FormEvent } from 'react';
import type { FormErrors } from '../../../hooks/useForm';

const EMPTY = {
  name: '', category: '', brand: '', specification: '', unit: 'nos', sacHsn: '', gstRate: 18,
  sellingRate: null, purchaseRate: null, minStockLevel: '', openingStock: '', openingRate: null, isActive: true,
};

const fromItem = (i: Item) => ({
  ...EMPTY,
  name: i.name ?? '', category: i.category ?? '', brand: i.brand ?? '', specification: i.specification ?? '',
  unit: i.unit || 'nos', sacHsn: i.sacHsn ?? '', gstRate: i.gstRate ?? 18, sellingRate: i.sellingRate ?? null,
  purchaseRate: i.purchaseRate ?? null, minStockLevel: i.minStockLevel ? String(i.minStockLevel) : '', isActive: i.isActive ?? true,
});

const decimal = (e: ChangeEvent<HTMLInputElement>) => e.target.value.replace(/[^\d.]/g, '');

/** Add / edit a stock material (wire, switch, MCB, fan…). */
export default function MaterialForm() {
  const { id = '' } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const toast = useToast();
  const { form, setForm, set, errors, setErrors } = useForm(EMPTY);
  const [saving, setSaving] = useState(false);

  const { data: item, loading, error, reload } = useFetch(
    () => (editing ? itemService.get(id) : Promise.resolve(null)),
    [id],
  );
  useCrumb(`/materials/${id}`, item?.name);

  useEffect(() => {
    if (item) setForm(fromItem(item));
  }, [item, setForm]);
  useMasters();

  const back = () => navigate(editing ? `/materials/${id}` : '/materials');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: FormErrors = {};
    if (!form.name.trim()) errs.name = 'Enter the material name';
    if (form.openingStock && !(Number(form.openingStock) > 0)) errs.openingStock = 'Enter a number more than 0';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const common = {
      name: form.name.trim(),
      category: trimOrUndef(form.category),
      brand: trimOrUndef(form.brand),
      specification: trimOrUndef(form.specification),
      unit: form.unit,
      sacHsn: trimOrUndef(form.sacHsn),
      gstRate: Number(form.gstRate),
      sellingRate: form.sellingRate ?? 0,
      purchaseRate: form.purchaseRate ?? 0,
      minStockLevel: Number(form.minStockLevel) || 0,
    };

    setSaving(true);
    try {
      const data = id
        ? await itemService.update(id, { ...common, isActive: form.isActive })
        : await itemService.create({
            ...common,
            type: 'material',
            ...(Number(form.openingStock) > 0 ? { openingStock: Number(form.openingStock), openingRate: form.openingRate ?? form.purchaseRate ?? 0 } : {}),
          });
      toast.success(editing ? 'Material updated' : 'Material added');
      navigate(`/materials/${editing ? id : data._id}`, { replace: true });
    } catch (err) {
      setErrors(serverFieldErrors(err));
      toast.error(err.message);
      setSaving(false);
    }
  };

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (editing && loading && !item) return <PageLoader />;

  const actions = (
    <>
      <Button variant="outline" onClick={back} disabled={saving}>Cancel</Button>
      <Button type="submit" form="material-form" leftIcon="check" loading={saving}>{editing ? 'Save changes' : 'Save material'}</Button>
    </>
  );
  const margin = (form.sellingRate ?? 0) - (form.purchaseRate ?? 0);
  const marginPct = form.purchaseRate ? Math.round((margin / form.purchaseRate) * 100) : null;

  return (
    <>
      <PageHeader title={editing ? `Edit ${item?.name}` : 'Add material'} subtitle={editing ? item?.itemCode : 'Stock items like wire, switches, MCBs and fans.'} actions={actions} />
      <form id="material-form" onSubmit={submit} noValidate>
        <TwoColumn
          main={
            <>
              <FormSection title="Material details">
                <Input label="Name" required autoFocus value={form.name} onChange={set('name')} error={errors.name} placeholder="e.g. Havells 2.5 sq mm FR wire, 90 m" containerClassName="sm:col-span-2" />
                <Select label="Category" options={masterOptions('materialCategory', { keep: form.category })} value={form.category || null} onChange={(v) => set('category')(v ?? '')} placeholder="Pick a category" clearable hint="Add categories in Master data" />
                <Input label="Brand" value={form.brand} onChange={set('brand')} placeholder="Optional" />
                <Input label="Specification" value={form.specification} onChange={set('specification')} placeholder="e.g. 1.5 sq mm, 6A" />
                <Select label="Unit" options={masterOptions('unit', { keep: form.unit })} value={form.unit} onChange={set('unit')} />
                <Select
                  label="HSN code"
                  options={masterOptions('hsnSac', { keep: form.sacHsn })}
                  value={form.sacHsn || null}
                  onChange={(v) => {
                    const hit = masterEntry('hsnSac', v);
                    setForm((f) => ({ ...f, sacHsn: v ?? '', ...(hit?.gstRate != null ? { gstRate: hit.gstRate } : {}) }));
                  }}
                  placeholder="Pick HSN code"
                  clearable
                  hint="Picking a code fills its GST rate"
                />
              </FormSection>

              <FormSection title="Rates and GST">
                <MoneyInput label="Selling rate" value={form.sellingRate} onChange={set('sellingRate')} placeholder="0" hint={`Per ${form.unit}, before GST`} />
                <MoneyInput label="Purchase rate" value={form.purchaseRate} onChange={set('purchaseRate')} placeholder="0" hint="Last price you paid" />
                <Select label="GST rate" options={masterOptions('gstRate', { numeric: true, keep: Number(form.gstRate) })} value={Number(form.gstRate)} onChange={set('gstRate')} searchable={false} />
              </FormSection>

              <FormSection title="Stock" subtitle={editing ? 'To change the quantity, use Stock > Adjust stock' : 'Only if you already have some in the shop'}>
                <Input label="Minimum level" inputMode="decimal" value={form.minStockLevel} onChange={(e) => set('minStockLevel')(decimal(e))} hint="Low stock alert at or below this" />
                {!editing && (
                  <>
                    <Input label="Opening stock" inputMode="decimal" value={form.openingStock} onChange={(e) => set('openingStock')(decimal(e))} error={errors.openingStock} placeholder="0" />
                    <MoneyInput label="Cost per unit" value={form.openingRate} onChange={set('openingRate')} placeholder="Same as purchase rate" />
                  </>
                )}
              </FormSection>

              {editing && (
                <Card className="p-5">
                  <Switch label="Active" description="Inactive materials are hidden when making quotations, bills and purchases" checked={form.isActive} onChange={set('isActive')} />
                </Card>
              )}
              <div className="flex justify-end gap-2">{actions}</div>
            </>
          }
          aside={
            <Card>
              <CardHeader title="Summary" />
              <div className="p-5 pt-4">
                <KeyValues
                  rows={[
                    ['Selling rate', `${money(form.sellingRate)} / ${form.unit}`],
                    ['Purchase rate', money(form.purchaseRate)],
                    ['Margin', `${money(margin)}${marginPct != null ? ` (${marginPct}%)` : ''}`, { tone: margin < 0 ? 'text-red-600' : 'text-emerald-700' }],
                    ['GST', `${form.gstRate}%`],
                  ]}
                />
              </div>
            </Card>
          }
        />
      </form>
    </>
  );
}
